import { type ImgHTMLAttributes } from 'react';

import { cn } from '../../utils';

export interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'size'> {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  initials?: string;
  status?: 'online' | 'offline' | 'away' | 'busy';
}

const sizeStyles = {
  xs: 'h-6 w-6 text-xs',
  sm: 'h-8 w-8 text-sm',
  md: 'h-10 w-10 text-base',
  lg: 'h-12 w-12 text-lg',
  xl: 'h-16 w-16 text-xl',
};

const statusStyles = {
  online: 'bg-success-500',
  offline: 'bg-tkf-line-medium',
  away: 'bg-warning-500',
  busy: 'bg-error-500',
};

const statusSizeStyles = {
  xs: 'h-1.5 w-1.5',
  sm: 'h-2 w-2',
  md: 'h-2.5 w-2.5',
  lg: 'h-3 w-3',
  xl: 'h-4 w-4',
};

export function Avatar({
  size = 'md',
  initials,
  status,
  src,
  alt,
  className,
  ...props
}: AvatarProps) {
  const showImage = src && !props.onError;

  return (
    <div className={cn('relative inline-flex', className)}>
      {showImage ? (
        <img
          src={src}
          alt={alt || 'Avatar'}
          className={cn('rounded-full object-cover', sizeStyles[size])}
          {...props}
        />
      ) : (
        <div
          className={cn(
            'flex items-center justify-center rounded-full bg-primary-100 font-medium text-primary-700',
            sizeStyles[size]
          )}
        >
          {initials?.slice(0, 2).toUpperCase() || '?'}
        </div>
      )}
      {status && (
        <span
          className={cn(
            'absolute bottom-0 right-0 block rounded-full ring-2 ring-tkf-surface',
            statusStyles[status],
            statusSizeStyles[size]
          )}
        />
      )}
    </div>
  );
}
